import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { FileText, FolderKanban, LifeBuoy, LogOut, Plus } from 'lucide-react';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Spinner } from '@/shared/components/ui/Spinner';
import { usePortalMe, usePortalMutations, usePortalOverview } from '../api';
import type { PortalTicket } from '../api';

const textareaClass =
  'w-full rounded border border-outline bg-surface px-3 py-2 text-data-md text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/40';

function errorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const maybe = err as { response?: { data?: { error?: string } } };
    if (maybe.response?.data?.error) return maybe.response.data.error;
  }
  return fallback;
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-IN', { dateStyle: 'medium' });
}

function ticketTone(status: string): BadgeTone {
  if (status === 'resolved' || status === 'closed') return 'success';
  if (status === 'waiting_requester') return 'warning';
  if (status === 'in_progress' || status === 'assigned') return 'info';
  return 'neutral';
}

function projectTone(status: string): BadgeTone {
  if (status === 'completed') return 'success';
  if (status === 'on_hold') return 'warning';
  if (status === 'cancelled') return 'danger';
  return 'info';
}

function invoiceTone(status: string): BadgeTone {
  if (status === 'paid') return 'success';
  if (status === 'overdue') return 'danger';
  if (status === 'sent') return 'info';
  return 'neutral';
}

function humanize(value: string): string {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/**
 * Standalone Client Portal (spec §1) — customers log in with portal accounts
 * (separate from staff sessions) and see only their own projects, invoices and
 * support tickets, scoped server-side.
 */
export function PortalPage() {
  const { data: me, isLoading, isError } = usePortalMe();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-variant/40">
        <Spinner />
      </div>
    );
  }
  if (isError || !me) return <PortalLogin />;
  return <PortalDashboard />;
}

/* ── Login ───────────────────────────────────────────────────────────────── */

function PortalLogin() {
  const { login } = usePortalMutations();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    login.mutate(
      { email: email.trim(), password },
      { onError: (err) => toast.error(errorMessage(err, 'Login failed.')) },
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-variant/40 p-4">
      <Card className="w-full max-w-sm">
        <CardBody className="flex flex-col gap-4 p-6">
          <div className="text-center">
            <p className="text-display-sm font-bold tracking-wide text-primary">
              ZYN<span className="text-[#7c3aed]">X</span>
            </p>
            <p className="mt-1 text-body-sm text-on-surface-variant">Client Portal</p>
          </div>
          <form onSubmit={submit} className="flex flex-col gap-3">
            <Input
              label="Email"
              name="portal-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label="Password"
              name="portal-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button type="submit" disabled={login.isPending || !email.trim() || !password}>
              {login.isPending ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <p className="text-center text-body-sm text-on-surface-variant">
            Access is provided by your account manager.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}

/* ── Dashboard ───────────────────────────────────────────────────────────── */

function PortalDashboard() {
  const { data, isLoading } = usePortalOverview(true);
  const { logout, raiseTicket } = usePortalMutations();
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [openTicket, setOpenTicket] = useState<PortalTicket | null>(null);

  const submitTicket = () => {
    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required.');
      return;
    }
    raiseTicket.mutate(
      { title: title.trim(), description: description.trim() },
      {
        onSuccess: (ticket) => {
          toast.success(`Ticket ${ticket.ticketNumber} raised.`);
          setTitle('');
          setDescription('');
          setTicketModalOpen(false);
        },
        onError: (err) => toast.error(errorMessage(err, 'Failed to raise ticket.')),
      },
    );
  };

  return (
    <div className="min-h-screen bg-surface-variant/40">
      <header className="border-b border-outline bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <p className="text-lg font-bold tracking-wide text-primary">
              ZYN<span className="text-[#7c3aed]">X</span>
            </p>
            <span className="text-body-sm text-on-surface-variant">Client Portal</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-body-sm text-on-surface">{data?.client.clientName}</span>
            <Button size="sm" variant="ghost" onClick={() => logout.mutate()}>
              <LogOut className="h-4 w-4" aria-hidden />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto flex max-w-6xl flex-col gap-4 p-4">
        {isLoading || !data ? (
          <div className="flex h-64 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="text-display-sm font-semibold text-on-surface">
                Welcome{data.client.contactPerson ? `, ${data.client.contactPerson}` : ''}
              </h1>
              <Button onClick={() => setTicketModalOpen(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                Raise Support Ticket
              </Button>
            </div>

            {/* Projects */}
            <Card>
              <CardHeader
                title={
                  <span className="flex items-center gap-2">
                    <FolderKanban className="h-4 w-4 text-primary" aria-hidden /> Your projects
                  </span>
                }
              />
              <CardBody>
                {data.projects.length === 0 ? (
                  <EmptyState title="No projects yet" description="Projects delivered for you will appear here." />
                ) : (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {data.projects.map((project) => {
                      const doneCount = project.milestones.filter((m) => m.done).length;
                      return (
                        <div key={project.id} className="rounded-lg border border-outline bg-surface p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-on-surface">{project.name}</span>
                            <Badge tone={projectTone(project.status)}>{humanize(project.status)}</Badge>
                          </div>
                          {project.description && (
                            <p className="mt-1 line-clamp-2 text-body-sm text-on-surface-variant">{project.description}</p>
                          )}
                          <p className="mt-1 text-body-sm text-on-surface-variant">
                            {project.startDate ? `Started ${formatDate(project.startDate)}` : ''}
                            {project.endDate ? ` · Target ${formatDate(project.endDate)}` : ''}
                          </p>
                          {project.milestones.length > 0 && (
                            <div className="mt-2">
                              <p className="text-body-sm text-on-surface-variant">
                                Milestones: {doneCount}/{project.milestones.length} complete
                              </p>
                              <ul className="mt-1 flex flex-col gap-1">
                                {project.milestones.map((m) => (
                                  <li key={m.id} className="flex items-center gap-2 text-body-sm">
                                    <span
                                      aria-hidden
                                      className={`h-2 w-2 rounded-full ${m.done ? 'bg-success' : 'bg-outline'}`}
                                    />
                                    <span className={m.done ? 'text-on-surface-variant line-through' : 'text-on-surface'}>
                                      {m.title}
                                      {m.dueDate ? ` (${formatDate(m.dueDate)})` : ''}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Invoices */}
            <Card>
              <CardHeader
                title={
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" aria-hidden /> Invoices
                  </span>
                }
              />
              <CardBody>
                {data.invoices.length === 0 ? (
                  <EmptyState title="No invoices" description="Invoices issued to you will appear here." />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[560px] text-left text-body-sm">
                      <caption className="sr-only">Your invoices</caption>
                      <thead className="text-on-surface-variant">
                        <tr>
                          <th scope="col" className="px-3 py-2 font-medium">Invoice</th>
                          <th scope="col" className="px-3 py-2 font-medium">Reference</th>
                          <th scope="col" className="px-3 py-2 font-medium">Amount</th>
                          <th scope="col" className="px-3 py-2 font-medium">Status</th>
                          <th scope="col" className="px-3 py-2 font-medium">Issued</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.invoices.map((invoice) => (
                          <tr key={invoice.id} className="border-t border-outline">
                            <td className="px-3 py-2 text-on-surface">{invoice.title}</td>
                            <td className="px-3 py-2 font-mono text-on-surface-variant">{invoice.referenceNumber || '—'}</td>
                            <td className="px-3 py-2 text-on-surface">₹{invoice.amount.toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2">
                              <Badge tone={invoiceTone(invoice.status)}>{humanize(invoice.status)}</Badge>
                            </td>
                            <td className="px-3 py-2 text-on-surface-variant">{formatDate(invoice.issuedAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Tickets */}
            <Card>
              <CardHeader
                title={
                  <span className="flex items-center gap-2">
                    <LifeBuoy className="h-4 w-4 text-primary" aria-hidden /> Support tickets
                  </span>
                }
              />
              <CardBody>
                {data.tickets.length === 0 ? (
                  <EmptyState
                    title="No tickets"
                    description="Raise a ticket and our support team will respond within the committed SLA."
                    action={<Button onClick={() => setTicketModalOpen(true)}>Raise Support Ticket</Button>}
                  />
                ) : (
                  <div className="flex flex-col gap-2">
                    {data.tickets.map((ticket) => (
                      <button
                        key={ticket.id}
                        onClick={() => setOpenTicket(ticket)}
                        className="rounded-lg border border-outline bg-surface p-3 text-left hover:bg-surface-variant/60"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-body-sm text-on-surface-variant">{ticket.ticketNumber}</span>
                          <span className="font-medium text-on-surface">{ticket.title}</span>
                          <Badge tone={ticketTone(ticket.status)}>{humanize(ticket.status)}</Badge>
                        </div>
                        <p className="mt-1 text-body-sm text-on-surface-variant">
                          Raised {formatDate(ticket.createdAt)}
                          {ticket.resolvedAt ? ` · Resolved ${formatDate(ticket.resolvedAt)}` : ''}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>
          </>
        )}
      </main>

      {/* Raise ticket modal */}
      <Modal
        open={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        title="Raise Support Ticket"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setTicketModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitTicket} disabled={raiseTicket.isPending}>
              {raiseTicket.isPending ? 'Raising…' : 'Raise Ticket'}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <Input label="Title" name="portal-ticket-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <div>
            <label htmlFor="portal-ticket-desc" className="mb-1.5 block text-body-sm font-medium text-on-surface">
              Describe the issue
            </label>
            <textarea
              id="portal-ticket-desc"
              rows={5}
              className={textareaClass}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <p className="text-body-sm text-on-surface-variant">
            Priority and routing are assigned automatically; response deadlines follow your support agreement.
          </p>
        </div>
      </Modal>

      {/* Ticket detail modal */}
      <Modal
        open={!!openTicket}
        onClose={() => setOpenTicket(null)}
        title={openTicket ? `${openTicket.ticketNumber} — ${openTicket.title}` : 'Ticket'}
      >
        {openTicket && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              <Badge tone={ticketTone(openTicket.status)}>{humanize(openTicket.status)}</Badge>
              <Badge tone="neutral">{humanize(openTicket.priority)}</Badge>
            </div>
            <p className="whitespace-pre-wrap text-body-sm text-on-surface">{openTicket.description}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-body-sm">
              <span className="text-on-surface-variant">First response due</span>
              <span className="text-on-surface">{formatDate(openTicket.firstResponseDueAt)}</span>
              <span className="text-on-surface-variant">Resolution due</span>
              <span className="text-on-surface">{formatDate(openTicket.resolutionDueAt)}</span>
            </div>
            {openTicket.resolutionSummary && (
              <div>
                <p className="text-body-sm font-medium text-on-surface">Resolution</p>
                <p className="text-body-sm text-on-surface-variant">{openTicket.resolutionSummary}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
